import React from 'react';
import { Link } from 'react-router-dom';
import { getPlanImageSources } from '../data/planPhotos';

interface PlaneCardProps {
  plane: {
    slug: string;
    id: string;
    name: string;
    date: string;
    location: string;
    apuntadas: number;
    creatorName: string;
    creatorAge: number;
    photoId: string;
    imagePosition: string;
  };
  imagePriority?: 'high' | 'low';
}

const PlaneCard: React.FC<PlaneCardProps> = ({ plane, imagePriority }) => {
  const { src, srcSet, imagePosition } = getPlanImageSources({
    slug: plane.slug,
    name: plane.name,
    photoId: plane.photoId,
    imagePosition: plane.imagePosition,
  });

  const date = new Date(plane.date);
  const fechaLine = date.toLocaleDateString('es-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const fechaCapitalized = fechaLine.charAt(0).toUpperCase() + fechaLine.slice(1);

  return (
    <Link
      to={`/${plane.slug}`}
      className="group block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-chisme-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#fcf7fa] rounded-3xl"
    >
      <article className="flex h-full min-h-0 flex-col overflow-hidden rounded-3xl border border-[rgba(74,33,71,0.1)] bg-white transition-colors duration-200 hover:border-chisme-primary/40">
        <div className="relative aspect-[16/10] w-full shrink-0 bg-gray-100">
          <img
            src={src}
            srcSet={srcSet}
            sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
            alt=""
            loading={imagePriority === 'high' ? 'eager' : 'lazy'}
            fetchPriority={imagePriority === 'high' ? 'high' : undefined}
            decoding="async"
            style={{ objectPosition: imagePosition }}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col justify-between p-5 sm:p-6">
          <header className="space-y-1">
            <h3 className="font-jakarta text-lg font-bold leading-tight tracking-tight text-[#2a0f2a] line-clamp-2 sm:text-xl">
              {plane.name}
            </h3>
            <p className="pt-0.5 font-jakarta text-sm leading-snug text-[#5a3158]">
              Creado por <span className="text-[#2a0f2a]">{plane.creatorName}</span> ({plane.creatorAge})
            </p>
          </header>

          <div className="mt-4 overflow-hidden rounded-2xl border border-[rgba(74,33,71,0.1)] bg-chisme-bg sm:mt-5">
            <div className="grid grid-cols-2 gap-0 border-b border-[rgba(74,33,71,0.1)]">
              <div className="border-r border-[rgba(74,33,71,0.1)] px-3 py-3 text-left sm:px-4 sm:py-3.5">
                <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                  Fecha
                </p>
                <p className="mt-1 break-words font-jakarta text-xs font-bold leading-tight text-[#2a0f2a] sm:text-sm">
                  {fechaCapitalized}
                </p>
              </div>
              <div className="px-3 py-3 text-left sm:px-4 sm:py-3.5">
                <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                  Apuntadas
                </p>
                <p className="mt-1 font-jakarta text-sm font-bold tabular-nums text-[#2a0f2a]">
                  {plane.apuntadas}
                </p>
              </div>
            </div>
            <div className="px-3 py-3 sm:px-4 sm:py-3.5">
              <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                Barrio
              </p>
              <p className="mt-1 break-words font-jakarta text-sm font-bold leading-snug text-[#2a0f2a]">{plane.location}</p>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-[rgba(74,33,71,0.1)] pt-4 sm:mt-5">
            <span className="font-jakarta text-sm font-semibold text-chisme-primary group-hover:underline underline-offset-4 decoration-chisme-primary/40">
              Ver detalles
            </span>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[rgba(74,33,71,0.1)] text-chisme-primary transition-colors duration-200 group-hover:border-chisme-primary/50 group-hover:bg-chisme-secondary/60">
              <svg className="-mr-px h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default PlaneCard;
